import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Icon,
  IconName,
  IconSize,
  Label,
  ProColors,
} from '@treatwell/treatwell-aura';
import { css } from '@emotion/react';
import { FormContextData, useFormContext } from '../../state';
import { BusinessSegment as BusinessSegmentType } from '../../api-client';
import { useValidator } from './helpers/use-validation';
import { CardVertical } from './components';

type BusinessSegmentCardProps = {
  icon: IconName;
  segment: BusinessSegmentType;
};

function BusinessSegmentCard(props: BusinessSegmentCardProps): JSX.Element {
  const { icon, segment } = props;
  const { t } = useTranslation('input', { keyPrefix: 'businessSegment' });

  const {
    data: { businessSegments },
    setData,
  } = useFormContext();

  const selected = businessSegments.some(s => s === segment);

  const onClick = useCallback(() => {
    setData(current => {
      const wasSelected = current.businessSegments.some(s => s === segment);
      if (wasSelected) {
        return {
          ...current,
          businessSegments: current.businessSegments.filter(s => s !== segment),
        };
      }
      return {
        ...current,
        businessSegments: [...current.businessSegments, segment],
      };
    });
  }, [segment, setData]);

  if (businessSegments[0] === segment) {
    return (
      <CardVertical
        icon={icon}
        text={t(segment) || ''}
        onClick={onClick}
        selected={selected}
        padding={16}
      >
        <div
          css={css({
            position: 'absolute',
            // match padding of CardVertical
            right: 16,
            top: 16,
          })}
        >
          <Label
            label={t('primary')}
            color={ProColors.teal[700]}
            backgroundColor={ProColors.teal[100]}
            size="xs"
            leftIcon={
              <Icon
                name={IconName.TW_PRO_HAPPY}
                size={IconSize.SMALL}
                color={ProColors.teal[700]}
              />
            }
          />
        </div>
      </CardVertical>
    );
  }

  return (
    <CardVertical
      icon={icon}
      text={t(segment) || ''}
      onClick={onClick}
      selected={selected}
      padding={16}
    />
  );
}

export function BusinessSegment(props: {
  onError: (error: string | null) => void;
}): JSX.Element {
  const { onError } = props;
  const { t } = useTranslation('input', { keyPrefix: 'businessSegment' });
  const { data } = useFormContext();

  const validate = useCallback(
    (data: FormContextData) =>
      data.businessSegments.length > 0 ? null : 'No BusinessSegment selected',
    [],
  );
  const wasValidated = useValidator(validate);
  const isValid = !validate(data);

  useEffect(() => {
    if (isValid) {
      onError(null);
    } else if (wasValidated) {
      onError(t('required'));
    }
  }, [onError, isValid, wasValidated, t]);

  return (
    <div>
      <Grid css={css({ gap: 16 })}>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_HAIR}
            segment={BusinessSegmentType.HAIRDRESSER}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_BEARD}
            segment={BusinessSegmentType.BARBER}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_NAILS}
            segment={BusinessSegmentType.NAILS}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_FACE}
            segment={BusinessSegmentType.AESTHETICS}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_SPA}
            segment={BusinessSegmentType.SPA}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_MASSAGE}
            segment={BusinessSegmentType.MASSAGE}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_HAIR_REMOVAL}
            segment={BusinessSegmentType.HAIR_REMOVAL}
          />
        </div>
        <div className="col-sm-12 col-md-6">
          <BusinessSegmentCard
            icon={IconName.TW_PRO_PRODUCTS}
            segment={BusinessSegmentType.BEAUTICIAN}
          />
        </div>
      </Grid>
    </div>
  );
}
